import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import MaterialThumbnailGroup from 'components/consumer/MaterialThumbnailGroup';
import {
  PDP_READYTOSHIP_TAB_ID,
  WOOD_STAIN_PRODUCT_OPTION_ID,
  BED_SIZE_PRODUCT_OPTION_ID,
} from 'commons/constants';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import MaterialThumbnailGroupPlaceholder from 'components/consumer/MaterialThumbnailGroup/MaterialThumbnailGroupPlaceholder';
import usePDPOptions from '../state/context/usePDPOptions';

const DEFAULT_OPTIONS = [];
const DEFAULT_SELECTED_OPTIONS = {};

const PDPImageSelector = ({ optionId }) => {
  const {
    loading,
    error,
    data: {
      options = DEFAULT_OPTIONS,
      selectedOptions = DEFAULT_SELECTED_OPTIONS,
      selectedOptionsExtended,
      stock,
    } = {},
    actions,
  } = usePDPOptions();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const [values, optionName] = useMemo(() => {
    let optName = 'material';

    const finalOptions =
      pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID
        ? stock.stockOptions
        : options;

    const optValIds = [];

    const vals =
      finalOptions
        ?.find(({ id, name }) => {
          if (id === optionId) {
            optName = name;
            return true;
          }
          return false;
        })
        ?.values?.map(v => {
          if (!optValIds.includes(v.id)) {
            optValIds.push(v.id);
            return v;
          }
          return v;
        })
        .filter(v => !!v) || [];

    return [vals, optName];
  }, [pdpSelectedCustomizerTab, stock?.stockOptions, options, optionId]);

  const { onOptionValueSelection } = actions;

  const selectedValueId = selectedOptions[optionId];

  const materials = useMemo(
    () =>
      values.map(({ id, value, image, description }) => ({
        materialId: id,
        label: value,
        image,
        upholsteryOptionId: optionId,
        description,
      })),
    [values, optionId]
  );

  const selectedMaterial = useMemo(
    () => materials.find(({ materialId }) => materialId === selectedValueId),
    [materials, selectedValueId]
  );

  const disabledMaterialIds = useMemo(() => {
    const ids = [];

    if (
      pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID &&
      materials.length
    ) {
      for (const material of materials) {
        if (stock?.stockOptionsDisabledStatus?.[material.materialId]) {
          ids.push(material.materialId);
        }
      }
    }

    return ids;
  }, [pdpSelectedCustomizerTab, materials, stock?.stockOptionsDisabledStatus]);

  const selectedOptionLabel =
    selectedOptionsExtended?.[optionId]?.value?.value || '';

  if (loading) {
    return 'loading...';
  }

  if (error) {
    return null;
  }

  return (
    <div
      className={`w-full ${
        optionId !== WOOD_STAIN_PRODUCT_OPTION_ID &&
        optionId !== BED_SIZE_PRODUCT_OPTION_ID &&
        materials.length < 2
          ? 'hidden'
          : ''
      }`}
    >
      <div className="text-base font-bold">{`Select ${optionName}`}</div>
      <div className="text-sm mb-2">{selectedOptionLabel}</div>

      {loading ? (
        <MaterialThumbnailGroupPlaceholder />
      ) : (
        <MaterialThumbnailGroup
          disabledMaterialIds={disabledMaterialIds}
          materials={materials}
          onClick={onOptionValueSelection}
          optionId={optionId}
          selectedMaterial={selectedMaterial}
        />
      )}
    </div>
  );
};

PDPImageSelector.propTypes = {
  optionId: PropTypes.number.isRequired,
};

export default PDPImageSelector;
